import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { toast } from "sonner";
import { MODELS, FILTERS } from "@/data/site";
import Reveal, { Overline } from "@/components/Reveal";
import ModelModal from "@/components/ModelModal";
import CompareModal from "@/components/CompareModal";
import { scrollToSection } from "@/lib/scroll";

export default function Lineup({ onBookModel }) {
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [compare, setCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const toggleCompare = (id) => {
    setCompare((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 2) {
        toast.error("Pick just two models to compare");
        return prev;
      }
      return [...prev, id];
    });
  };

  const activeFilter = FILTERS.find((f) => f.id === filter);
  const models = MODELS.filter(activeFilter.match);

  const book = (m) => {
    onBookModel(m.id);
    setActive(null);
    scrollToSection("#test-drive");
  };

  return (
    <section id="lineup" data-testid="lineup-section" className="relative bg-[#05141F] py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Overline>The Collection</Overline>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
            <h2 className="font-display font-extrabold tracking-tight leading-[1.02] text-3xl sm:text-5xl text-white max-w-xl">
              Eleven Machines.<br />
              <span className="text-outline">One Obsession.</span>
            </h2>
            <p className="text-slate-400 text-sm sm:text-base max-w-sm leading-relaxed">
              Every Kia sold in India — from the compact Syros to the World Car of the Year EV9. Filter by what moves you.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.15}>
          <div data-testid="lineup-filter-tabs" className="mt-10 flex flex-wrap gap-2.5">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`lineup-filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`font-mono text-[11px] uppercase tracking-[0.22em] px-5 py-2.5 border transition-colors duration-300 ${
                  filter === f.id
                    ? "bg-[#FFFFFF] border-[#FFFFFF] text-black"
                    : "border-white/15 text-slate-400 hover:border-white/40 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {models.map((m, i) => (
              <motion.article
                layout
                key={m.id}
                data-testid={`model-card-${m.id}`}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.96 }}
                transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group relative bg-[#0D1822] border border-white/10 hover:border-[#FFFFFF]/50 transition-colors duration-500 clip-card overflow-hidden"
              >
                <div className="relative h-52 overflow-hidden card-spotlight">
                  <img
                    src={m.image}
                    alt={m.name}
                    loading="lazy"
                    className={`w-full h-full transition-transform duration-700 ease-out group-hover:scale-110 ${
                      m.fit === "cover" ? "object-cover" : "object-contain p-6"
                    }`}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0D1822] via-transparent to-transparent" />
                  <span
                    data-testid={`model-badge-${m.id}`}
                    className="absolute top-4 left-4 font-mono text-[9px] uppercase tracking-[0.25em] bg-[#FFFFFF] text-black px-3 py-1.5"
                  >
                    {m.badge}
                  </span>
                  <span className="absolute top-4 right-4 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-300 bg-black/40 backdrop-blur-md border border-white/10 px-3 py-1.5">
                    {m.category}
                  </span>
                </div>

                <div className="p-6">
                  <h3 className="font-display font-extrabold text-2xl text-white tracking-tight">{m.name}</h3>
                  <p className="mt-1 text-sm text-slate-500 italic">{m.tagline}</p>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">From</span>
                    <span data-testid={`model-price-${m.id}`} className="font-display font-bold text-xl text-[#FFFFFF]">{m.price}</span>
                  </div>
                  <div className="mt-6 flex gap-3">
                    <button
                      data-testid={`model-specs-button-${m.id}`}
                      onClick={() => setActive(m)}
                      className="flex-1 inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/50 text-white text-xs font-semibold uppercase tracking-[0.15em] py-3 transition-colors duration-300"
                    >
                      View Specs <ArrowUpRight size={14} />
                    </button>
                    <button
                      data-testid={`model-book-button-${m.id}`}
                      onClick={() => book(m)}
                      className="flex-1 bg-[#FFFFFF] hover:bg-[#D4D4D4] text-black text-xs font-semibold uppercase tracking-[0.15em] py-3 transition-colors duration-300"
                    >
                      Test Drive
                    </button>
                  </div>
                  <button
                    data-testid={`model-compare-toggle-${m.id}`}
                    onClick={() => toggleCompare(m.id)}
                    className={`mt-3 w-full font-mono text-[10px] uppercase tracking-[0.2em] py-2 border transition-colors duration-300 ${
                      compare.includes(m.id)
                        ? "border-[#FFFFFF] text-[#FFFFFF]"
                        : "border-white/10 text-slate-500 hover:border-white/30 hover:text-white"
                    }`}
                  >
                    {compare.includes(m.id) ? "✓ Added to Compare" : "+ Compare"}
                  </button>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <ModelModal model={active} onClose={() => setActive(null)} onBook={book} />

      <AnimatePresence>
        {compare.length > 0 && (
          <motion.div
            data-testid="compare-tray"
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 flex items-center gap-4 bg-[#0D1822]/95 backdrop-blur-xl border border-white/15 px-5 py-3.5 clip-notch shadow-2xl"
          >
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              {compare.map((id) => MODELS.find((m) => m.id === id)?.name.replace("Kia ", "")).join(" vs ")}
              {compare.length === 1 ? " — pick one more" : ""}
            </span>
            <button
              data-testid="compare-now-button"
              onClick={() => compare.length === 2 && setCompareOpen(true)}
              disabled={compare.length !== 2}
              className="bg-[#FFFFFF] hover:bg-[#D4D4D4] disabled:opacity-40 disabled:pointer-events-none text-black text-[11px] font-semibold uppercase tracking-[0.15em] px-4 py-2 transition-colors"
            >
              Compare
            </button>
            <button
              data-testid="compare-clear-button"
              onClick={() => setCompare([])}
              className="text-slate-500 hover:text-white transition-colors"
              aria-label="Clear comparison"
            >
              <X size={15} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <CompareModal
        open={compareOpen}
        models={compare.map((id) => MODELS.find((m) => m.id === id)).filter(Boolean)}
        onClose={() => setCompareOpen(false)}
      />
    </section>
  );
}
